import { useState, useEffect } from 'react';
import { getServicesAdmin, createService, updateService, deleteService, updateServiceOrder } from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import AdminPageSkeleton from '../components/AdminPageSkeleton';
import ConfirmModal from '../components/ConfirmModal';
import Error500 from './errors/Error500';
import './AdminPages.css';

const AdminServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    nazov: '',
    popis: '',
    cena: '',
    trvanie_minuty: '',
    cas_od: '',
    cas_do: '',
    aktivna: true,
  });
  const [modalError, setModalError] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredServices(services);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredServices(
        services.filter(service => 
          service.nazov.toLowerCase().includes(query) ||
          (service.popis && service.popis.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, services]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await getServicesAdmin();
      const servicesData = response.data.data || response.data || [];
      setServices(servicesData);
      setFilteredServices(servicesData);
      setError(null);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load services:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      setError('Nepodarilo sa načítať služby. Skúste to prosím neskôr.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      const updatedData = {
        name: service.nazov,
        description: service.popis,
        duration_minutes: service.trvanie_minuty,
        price: service.cena,
        is_active: !service.je_aktivna
      };
      
      await updateService(service.id, updatedData);
      
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, je_aktivna: !s.je_aktivna } : s
      ));
    } catch (err) {
      console.error('Failed to toggle service:', err);
      alert('Nepodarilo sa zmeniť stav služby');
    }
  };

  const handleAddService = () => {
    setModalMode('add');
    setModalError('');
    setFormData({
      nazov: '',
      popis: '',
      cena: '',
      trvanie_minuty: '',
      cas_od: '',
      cas_do: '',
      aktivna: true,
    });
    setShowModal(true);
  };

  const handleEditService = (service) => {
    setModalMode('edit');
    setModalError('');
    setEditingService(service);
    setFormData({
      nazov: service.nazov,
      popis: service.popis || '',
      cena: service.cena,
      trvanie_minuty: service.trvanie_minuty,
      cas_od: service.cas_od || '',
      cas_do: service.cas_do || '',
      aktivna: service.je_aktivna === 1 || service.je_aktivna === true,
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');

    try {
      const apiData = {
        name: formData.nazov,
        description: formData.popis,
        duration_minutes: parseInt(formData.trvanie_minuty),
        price: parseFloat(formData.cena),        cas_od: formData.cas_od || null,
        cas_do: formData.cas_do || null,        is_active: formData.aktivna
      };

      if (modalMode === 'add') {
        const response = await createService(apiData);
        const newService = response.data.data || response.data;
        setServices(prev => [...prev, newService]);
      } else {
        await updateService(editingService.id, apiData);
        setServices(prev => prev.map(s => 
          s.id === editingService.id 
            ? { ...s, ...formData, trvanie_minuty: parseInt(formData.trvanie_minuty), je_aktivna: formData.aktivna ? 1 : 0 }
            : s
        ));
      }
      
      closeModalWithAnimation(() => {
        setShowModal(false);
      });
    } catch (err) {
      console.error('Failed to save service:', err);
      setModalError(err.response?.data?.message || 'Nepodarilo sa uložiť službu');
    }
  };

  const handleCloseModal = () => {
    closeModalWithAnimation(() => setShowModal(false));
  };

  const handleCloseConfirmModal = () => {
    closeModalWithAnimation(() => setShowConfirmModal(false));
  };

  const handleDeleteService = async (service) => {
    setServiceToDelete(service);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;
    
    try {
      await deleteService(serviceToDelete.id);
      loadServices();
      setShowConfirmModal(false);
      setServiceToDelete(null);
    } catch (err) {
      console.error('Failed to delete service:', err);
      alert('Nepodarilo sa vymazať službu');
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newServices = [...filteredServices];
    const draggedItem = newServices[draggedIndex];
    
    newServices.splice(draggedIndex, 1);
    newServices.splice(dropIndex, 0, draggedItem);
    
    setFilteredServices(newServices);
    setServices(newServices);
    setDraggedIndex(null);

    try {
      const orderedIds = newServices.map(s => s.id);
      await updateServiceOrder(orderedIds);
      console.log('Service order updated successfully');
    } catch (err) {
      console.error('Failed to update service order:', err);
      loadServices();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Ponúkané služby"
        subtitle="Správa služieb a cenníka"
        variant="services"
      />
    );
  }

  if (serverError) {
    return <Error500 />;
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="alert alert-error mb-3">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-page services-page">
      <div className="page-header">
        <div>
          <h1>Ponúkané služby</h1>
          <p className='text-muted'>Správa služieb a cenníka</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary btn-lg" onClick={handleAddService}>Pridať službu</button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="services-toolbar">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Hľadať služby..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>×</button>
          )}
        </div>
        <div className="services-count">
          {filteredServices.length} {filteredServices.length === 1 ? 'služba' : filteredServices.length < 5 ? 'služby' : 'služieb'}
        </div>
      </div>

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <div className="table-container">
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>Žiadne služby</p>
          </div>
        </div>
      ) : (
        <div className="services-list">
          {filteredServices.map((service, index) => (
            <div 
              key={service.id} 
              className={`service-row ${!service.je_aktivna ? 'inactive' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              {/* Drag Handle */}
              <div className="drag-handle" title="Podržte a ťahajte pre zmenu poradia">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                </svg>
              </div>

              {/* Service Info */}
              <div className="service-info">
                <h3 className="service-title">{service.nazov}</h3>
                {service.popis && (
                  <p className="service-desc">{service.popis.length > 80 ? service.popis.substring(0, 80) + '...' : service.popis}</p>
                )}
              </div>

              {/* Duration */}
              <div className="service-duration">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                </svg>
                <span>{service.trvanie_minuty} min</span>
              </div>

              {/* Price */}
              {service.cena && (
                <div className="service-price">
                  <span className="price-value">{service.cena} €</span>
                </div>
              )}

              {/* Toggle Switch */}
              <div className="service-toggle">
                <label className="switch-container" title={service.je_aktivna ? 'Deaktivovať službu' : 'Aktivovať službu'}>
                  <input
                    type="checkbox"
                    checked={service.je_aktivna}
                    onChange={() => handleToggleActive(service)}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>

              {/* Actions */}
              <div className="service-actions">
                <button 
                  className="btn-icon btn-warning" 
                  onClick={() => handleEditService(service)}
                  title="Upraviť službu"
                >
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.9888 4.28491L19.6405 2.93089C18.4045 1.6897 16.4944 1.6897 15.2584 2.93089L13.0112 5.30042L18.7416 11.055L21.1011 8.68547C21.6629 8.1213 22 7.33145 22 6.54161C22 5.75176 21.5506 4.84908 20.9888 4.28491Z"/>
                    <path d="M16.2697 10.9422L11.7753 6.42877L2.89888 15.3427C2.33708 15.9069 2 16.6968 2 17.5994V21.0973C2 21.5487 2.33708 22 2.89888 22H6.49438C7.2809 22 8.06742 21.6615 8.74157 21.0973L17.618 12.1834L16.2697 10.9422Z"/>
                  </svg>
                </button>
                <button 
                  className="btn-icon btn-delete" 
                  onClick={() => handleDeleteService(service)}
                  title="Vymazať službu"
                >
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-service" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'add' ? 'Pridať službu' : 'Upraviť službu'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {modalError && (
                  <div className="alert alert-error mb-3">✕ {modalError}</div>
                )}

                <div className="form-grid-modern">
                  {/* Názov služby */}
                  <div className={`form-group-floating full-width ${formData.nazov ? 'has-value' : ''}`} style={{order: 1}}>
                    <input
                      type="text"
                      name="nazov"
                      id="nazov"
                      className="form-input-floating"
                      value={formData.nazov}
                      onChange={handleFormChange}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="nazov" className="form-label-floating">Názov služby</label>
                  </div>

                  {/* Cena */}
                  <div className={`form-group-floating ${formData.cena ? 'has-value' : ''}`} style={{order: 2}}>
                    <input
                      type="number"
                      name="cena"
                      id="cena"
                      className="form-input-floating"
                      value={formData.cena}
                      onChange={handleFormChange}
                      min="0"
                      step="1"
                      placeholder=" "
                    />
                    <label htmlFor="cena" className="form-label-floating">Cena (€) <span className="optional">(voliteľné)</span></label>
                  </div>

                  {/* Trvanie */}
                  <div className={`form-group-floating ${formData.trvanie_minuty ? 'has-value' : ''}`} style={{order: 3}}>
                    <input
                      type="number"
                      name="trvanie_minuty"
                      id="trvanie_minuty"
                      className="form-input-floating"
                      value={formData.trvanie_minuty}
                      onChange={handleFormChange}
                      min="1"
                      step="1"
                      placeholder=" "
                      required
                    />
                    <label htmlFor="trvanie_minuty" className="form-label-floating">Trvanie (minúty)</label>
                  </div>

                  {/* Časové okno OD */}
                  <div className={`form-group-floating ${formData.cas_od ? 'has-value' : ''}`} style={{order: 3}}>
                    <input
                      type="time"
                      name="cas_od"
                      id="cas_od"
                      className="form-input-floating"
                      value={formData.cas_od}
                      onChange={handleFormChange}
                      placeholder=" "
                    />
                    <label htmlFor="cas_od" className="form-label-floating">Dostupné od <span className="optional">(voliteľné)</span></label>
                  </div>

                  {/* Časové okno DO */}
                  <div className={`form-group-floating ${formData.cas_do ? 'has-value' : ''}`} style={{order: 3}}>
                    <input
                      type="time"
                      name="cas_do"
                      id="cas_do"
                      className="form-input-floating"
                      value={formData.cas_do}
                      onChange={handleFormChange}
                      placeholder=" "
                    />
                    <label htmlFor="cas_do" className="form-label-floating">Dostupné do <span className="optional">(voliteľné)</span></label>
                  </div>

                  {/* Time window info */}
                  {(formData.cas_od || formData.cas_do) && (
                    <div className="form-info full-width" style={{order: 3}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span>Zákazníci si budú môcť rezervovať túto službu len v zadanom časovom okne</span>
                    </div>
                  )}

                  {/* Popis */}
                  <div className={`form-group-floating full-width ${formData.popis ? 'has-value' : ''}`} style={{order: 4}}>
                    <textarea
                      name="popis"
                      id="popis"
                      className="form-input-floating"
                      rows="3"
                      value={formData.popis}
                      onChange={handleFormChange}
                      placeholder=" "
                    />
                    <label htmlFor="popis" className="form-label-floating">Popis <span className="optional">(voliteľné)</span></label>
                  </div>

                  {/* Aktívna služba - len pri editácii */}
                  {modalMode === 'edit' && (
                    <div className="form-group-floating full-width" style={{order: 5}}>
                      <label className="form-checkbox">
                        <input
                          type="checkbox"
                          name="aktivna"
                          checked={formData.aktivna}
                          onChange={handleFormChange}
                        />
                        <span>Služba je aktívna</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Zrušiť
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalMode === 'add' ? 'Vytvoriť' : 'Uložiť zmeny'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={confirmDelete}
        title="Potvrdenie vymazania"
        message={`Naozaj chcete vymazať službu "${serviceToDelete?.nazov}"?`}
        confirmText="Vymazať"
        cancelText="Zrušiť"
        confirmButtonClass="btn-danger"
      />
    </div>
  );
};

export default AdminServicesPage;
